/**
 * Field Manager Utility
 * Handles loading and structuring field metadata from Lark tables
 * Supports nested linked fields up to a specified depth
 */

import { bitable, FieldType, type ICommonLinkFieldProperty } from '@lark-base-open/js-sdk'
import type { FieldMetadata, GetRecordDataOptions } from '@/types'

/**
 * Sanitize field name to create valid variable name
 * Replaces spaces, dots, and other special characters with underscores
 */
export function sanitizeFieldName(fieldName: string): string {
  return fieldName
    .replace(/[\s.]+/g, '_')  // Replace spaces and dots with underscore
    .replace(/[^\w]/g, '_')   // Replace any other non-word characters with underscore
}

/**
 * Load nested fields from a linked table recursively
 */
export async function loadNestedFields(
  tableId: string,
  prefix: string = '',
  currentDepth: number = 0,
  maxDepth: number = 5,
  visitedTables: Set<string> = new Set()
): Promise<FieldMetadata[]> {
  // Prevent infinite recursion
  if (currentDepth >= maxDepth) {
    return []
  }

  const newVisitedTables = new Set(visitedTables)
  newVisitedTables.add(tableId)

  try {
    const linkedTable = await bitable.base.getTableById(tableId)
    const linkedFieldMeta = await linkedTable.getFieldMetaList()
    const fields: FieldMetadata[] = []

    for (const field of linkedFieldMeta) {
      const fieldName = sanitizeFieldName(field.name)

      // Handle linked fields recursively
      if (field.type === FieldType.SingleLink) {
        const linkProperty = field.property as ICommonLinkFieldProperty
        if (!linkProperty?.tableId) continue

        const isMultiple = linkProperty.multiple === true

        // Always add parent prefix to the field itself (both single-link and multi-link)
        const variable = prefix ? `${prefix}.${fieldName}` : fieldName

        // For multi-link (loops), use empty prefix so nested fields are clean (e.g., {Payment_terms})
        // For single-link, use the variable as prefix (e.g., {Customer_name.Address})
        const nestedPrefix = isMultiple ? '' : variable
        const nestedFields = await loadNestedFields(
          linkProperty.tableId,
          nestedPrefix,
          currentDepth + 1,
          maxDepth,
          newVisitedTables
        )

        fields.push({
          name: field.name,
          variable: variable,
          type: field.type,
          isLinked: true,
          isMultiple: isMultiple,
          nestedVariables: nestedFields,
          depth: currentDepth
        })
      } else {
        // Regular field - always add prefix for non-linked fields
        const variable = prefix ? `${prefix}.${fieldName}` : fieldName
        fields.push({
          name: field.name,
          variable: variable,
          type: field.type,
          isLinked: false,
          isMultiple: false,
          depth: currentDepth
        })
      }
    }

    return fields
  } catch (error) {
    console.error(`Failed to load nested fields for table ${tableId}:`, error)
    return []
  }
}

/**
 * Load all fields from the current table including nested linked fields
 */
export async function loadAllFields(
  tableId: string,
  maxDepth: number = 5,
  includeSystemVars: boolean = true
): Promise<FieldMetadata[]> {
  try {
    // Use loadNestedFields to get all fields
    const fields = await loadNestedFields(tableId, '', 0, maxDepth, new Set())

    // Add system variables if requested
    if (includeSystemVars) {
      fields.push(...getSystemVariables())
    }

    return fields
  } catch (error) {
    console.error('Failed to load fields:', error)
    return []
  }
}

/**
 * Get system variables that are always available
 */
export function getSystemVariables(): FieldMetadata[] {
  return [
    {
      name: 'Record_ID',
      variable: 'Record_ID',
      isSystem: true,
      depth: 0
    },
    {
      name: 'Template_Name',
      variable: 'Template_Name',
      isSystem: true,
      depth: 0
    },
    {
      name: 'Table_Name',
      variable: 'Table_Name',
      isSystem: true,
      depth: 0
    },
    {
      name: 'Print_Time',
      variable: 'Print_Time',
      isSystem: true,
      depth: 0
    }
  ]
}

/**
 * Flatten field tree to a list (excluding multi-link/loop fields)
 * Useful for filename pattern variables where loops don't make sense
 */
export function flattenFieldsForFilename(fields: FieldMetadata[], result: FieldMetadata[] = []): FieldMetadata[] {
  for (const field of fields) {
    // Exclude multi-link fields (loops)
    if (field.isMultiple) continue

    // If it's a single-link field with nested variables, expand them
    if (field.isLinked && !field.isMultiple && field.nestedVariables && field.nestedVariables.length > 0) {
      // Add the nested fields recursively
      flattenFieldsForFilename(field.nestedVariables, result)
    } else if (!field.isLinked || !field.nestedVariables || field.nestedVariables.length === 0) {
      // Add regular fields and system fields
      result.push(field)
    }
  }
  return result
}

/**
 * Filter fields recursively based on search query
 */
export function filterFieldsByQuery(fields: FieldMetadata[], query: string): FieldMetadata[] {
  if (!query) return fields

  const lowerQuery = query.toLowerCase()

  const filterRecursive = (fieldList: FieldMetadata[]): FieldMetadata[] => {
    return fieldList.filter(field => {
      const matchesSearch = field.name.toLowerCase().includes(lowerQuery) ||
                           field.variable.toLowerCase().includes(lowerQuery)

      if (field.nestedVariables && field.nestedVariables.length > 0) {
        const filteredNested = filterRecursive(field.nestedVariables)
        if (filteredNested.length > 0) {
          return true
        }
      }

      return matchesSearch
    }).map(field => {
      if (field.nestedVariables && field.nestedVariables.length > 0) {
        return {
          ...field,
          nestedVariables: filterRecursive(field.nestedVariables)
        }
      }
      return field
    })
  }

  return filterRecursive(fields)
}

/**
 * Get record data with all field values (including nested linked records)
 * Returns formatted data ready for document generation
 */
export async function getRecordData(
  tableId: string,
  recordId: string,
  options: GetRecordDataOptions = {}
): Promise<Record<string, any>> {
  const { maxDepth = 5, templateName = null } = options
  const { formatFieldValue, formatLinkedRecord } = await import('./fieldFormatter')

  try {
    const table = await bitable.base.getTableById(tableId)
    const record = await table.getRecordById(recordId)
    const fieldMetaList = await table.getFieldMetaList()
    const tableMeta = await table.getMeta()

    const formattedData: Record<string, any> = {}

    // Track visited tables to prevent circular references
    const visitedTables = new Set<string>([tableId])

    for (const field of fieldMetaList) {
      const fieldValue = record.fields[field.id]
      const variableName = sanitizeFieldName(field.name)

      // Handle linked fields with depth support
      if (field.type === FieldType.SingleLink && fieldValue && typeof fieldValue === 'object') {
        const linkProperty = field.property as ICommonLinkFieldProperty
        const recordIds = (fieldValue as any).recordIds || (fieldValue as any).record_ids || []
        const linkedTableId = (fieldValue as any).tableId || (fieldValue as any).table_id
        const isMultiple = linkProperty?.multiple === true

        if (linkedTableId && !visitedTables.has(linkedTableId) && recordIds.length > 0) {
          try {
            const linkedTable = await bitable.base.getTableById(linkedTableId)
            const linkedFieldMeta = await linkedTable.getFieldMetaList()

            const newVisitedTables = new Set(visitedTables)
            newVisitedTables.add(linkedTableId)

            const linkedRecords = []
            for (const linkedRecordId of recordIds) {
              try {
                const linkedRecordValue = await linkedTable.getRecordById(linkedRecordId)
                const linkedRecord = { recordId: linkedRecordId, fields: linkedRecordValue.fields }
                // Use formatLinkedRecord with depth control
                const linkedData = await formatLinkedRecord(
                  linkedRecord,
                  linkedFieldMeta,
                  0,
                  maxDepth,
                  newVisitedTables
                )
                linkedRecords.push(linkedData)
              } catch (err) {
                console.error(`Failed to load nested record ${linkedRecordId}:`, err)
              }
            }

            formattedData[variableName] = isMultiple ? linkedRecords : linkedRecords[0] || null
          } catch (err) {
            console.error(`Failed to load linked field ${field.name}:`, err)
            formattedData[variableName] = isMultiple ? [] : null
          }
        } else {
          // Circular reference or max depth
          formattedData[variableName] = isMultiple ? [] : null
        }
      } else {
        // Regular field
        formattedData[variableName] = formatFieldValue(fieldValue, field.type, field.property)
      }
    }

    // Add system variables (matching getSystemVariables)
    formattedData.Record_ID = recordId
    formattedData.Template_Name = templateName || 'Template'
    formattedData.Table_Name = tableMeta.name
    formattedData.Print_Time = new Date().toLocaleString()

    return formattedData
  } catch (error) {
    console.error('Failed to get record data:', error)
    throw error
  }
}
